import { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Tabs,
  Badge,
  Button,
  Toggle,
  Skeleton,
  Alert,
  ConfirmDialog,
  Dropdown,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { tenantService, isQueuedApproval } from '@/services/tenant.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { toCurrencyCode, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import { TenantStatusBadge } from '@/components/features/tenants/TenantStatusBadge';
import { TenantProvisionBanner } from '@/components/features/tenants/TenantProvisionBanner';
import { TenantOverviewTab } from '@/components/features/tenants/TenantOverviewTab';
import { TenantSubscriptionTab } from '@/components/features/tenants/TenantSubscriptionTab';
import { TenantLocationsTab } from '@/components/features/tenants/TenantLocationsTab';
import { TenantUsersTab } from '@/components/features/tenants/TenantUsersTab';
import { TenantAuditTab } from '@/components/features/tenants/TenantAuditTab';
import { TenantWebsiteTab } from '@/components/features/tenants/TenantWebsiteTab';
import { TenantUsageCard } from '@/components/features/tenants/TenantUsageCard';
import {
  GLASS_PANEL,
  cn,
  KpiStatCard,
  type KpiTone,
} from '@/components/features/tenants/tenant-ui';

// ─── Feature flag config ────────────────────────────────────────────────────
const ALL_FEATURE_FLAGS = [
  { key: 'ENABLE_KDS', label: 'Kitchen Display System', description: 'KDS for restaurant order routing' },
  { key: 'ENABLE_FESTIVE_MODULE', label: 'Festive Orders', description: 'Seasonal/festive order workflow' },
  { key: 'ENABLE_VOICE_POS', label: 'Voice POS', description: 'Voice-driven POS in regional languages' },
  { key: 'ENABLE_CREDIT_BILLING', label: 'Credit Billing', description: 'Credit-based billing for customers' },
  { key: 'ENABLE_DISPATCH', label: 'Dispatch Management', description: 'Delivery dispatch tracking' },
  { key: 'ENABLE_MATERIAL_INVENTORY', label: 'Material Inventory', description: 'Material/manufacturing inventory' },
];

// ─── Role level constants ───────────────────────────────────────────────────
const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  MODERATOR: 60,
  OWNER: 50,
  MANAGER: 30,
  CASHIER: 5,
};

// Platform view — tenant catalogue is NOT enumerated here (issue #303).
// Product/category volume is surfaced as KPI totals instead; per-SKU price,
// stock and status are operational tenant data with no platform-admin purpose.
// Matches docs/sdlc/phase5/PAGES.md §3.5 (Overview / Locations / Users /
// Subscription / Audit).
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'users', label: 'Users' },
  { id: 'locations', label: 'Locations' },
  { id: 'subscription', label: 'Subscription' },
  { id: 'flags', label: 'Feature Flags' },
  { id: 'site', label: 'Website' },
  { id: 'audit', label: 'Audit Log' },
];

// ─── KPI card ───────────────────────────────────────────────────────────────
/**
 * "of 3" under the count, or an explicit over-limit note for a grandfathered
 * tenant that already exceeds a ceiling set after the fact. Undefined while the
 * limits query is still in flight, or when the tenant has no ceiling at all.
 */
function limitSupporting(used: number | undefined, limit: number | null | undefined): string | undefined {
  if (limit == null || used == null) return undefined;
  if (used > limit) return `Over limit — ${used} of ${limit}`;
  if (used === limit) return `At limit — ${limit} of ${limit}`;
  return `of ${limit}`;
}

/** Amber approaching the ceiling, red at or over it, otherwise the card's own tone. */
function limitTone(used: number | undefined, limit: number | null | undefined, base: KpiTone): KpiTone {
  if (limit == null || used == null) return base;
  if (used >= limit) return 'danger';
  if (used / limit >= 0.8) return 'warning';
  return base;
}

// ─── Icons ──────────────────────────────────────────────────────────────────
function MapPinIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}
function UsersIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}
function CurrencyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
function CubeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10m0-10L4 7v10l8 4" />
    </svg>
  );
}
function TagIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5a2 2 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 013 10V5a2 2 0 012-2z" />
    </svg>
  );
}
function ReceiptIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
    </svg>
  );
}

// ─── Content ────────────────────────────────────────────────────────────────

function TenantDetailContent() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const user = useAuthStore((s) => s.user);
  const userRoleLevel = ROLE_LEVELS[user?.role ?? ''] ?? 0;
  const isAdminPlus = userRoleLevel >= 80; // ADMIN (80) or SUPER_ADMIN (100)
  const isSuperAdmin = userRoleLevel >= 100;

  // Fall back to Overview for an unknown tab id — bookmarks pointing at the
  // removed ?tab=products / ?tab=categories views would otherwise render blank.
  const requestedTab = searchParams.get('tab') ?? 'overview';
  const activeTab = TABS.some((t) => t.id === requestedTab) ? requestedTab : 'overview';
  const [confirmAction, setConfirmAction] = useState<'suspend' | 'cancel' | 'reactivate' | null>(null);
  const [suspendReason, setSuspendReason] = useState('');

  const {
    data: tenant,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => tenantService.getById(id!),
    enabled: Boolean(id),
  });

  // Ceilings for the Locations / Users cards. A failure here must not blank the
  // page — the cards fall back to a bare count.
  const { data: limits } = useQuery({
    queryKey: ['tenant-limits', id],
    queryFn: () => tenantService.getLimits(id!),
    enabled: Boolean(id),
  });

  const provisionMutation = useMutation({
    mutationFn: () => tenantService.provision(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
      toast({ title: 'Tenant approved and provisioned', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  const statusMutation = useMutation({
    mutationFn: ({ status, reason }: { status: string; reason?: string }) =>
      tenantService.updateStatus(id!, status as never, reason),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
      setConfirmAction(null);
      setSuspendReason('');
      // Cancellation is a critical transition: the API only records an approval
      // request and leaves the tenant status alone. Reporting "updated" here
      // told the admin the account was cancelled when nothing had changed.
      if (isQueuedApproval(result)) {
        toast({
          title: 'Sent for Admin approval',
          description:
            'The tenant status is unchanged until the request is approved under Approvals.',
          variant: 'info',
        });
        return;
      }
      toast({ title: 'Tenant status updated', variant: 'success' });
    },
    onError: (err: Error & { code?: string }) => {
      if (err.code === 'TENANT_INVALID_STATUS_TRANSITION') {
        toast({ title: 'Invalid status transition — cannot perform this action', variant: 'error' });
      } else {
        toast({ title: err.message, variant: 'error' });
      }
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4 animate-fade-in" role="status" aria-label="Loading tenant details">
        <Skeleton variant="rect" height={120} />
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={140} />
          ))}
        </div>
        <Skeleton variant="rect" height={40} />
        <Skeleton variant="rect" height={300} />
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <Alert
        variant="danger"
        title="Failed to load tenant"
        description="The tenant may not exist or you lack permission to view it."
        action={{ label: 'Back to Tenants', onClick: () => navigate('/tenants') }}
      />
    );
  }

  const endDate = tenant.subscription_expires_at ? new Date(tenant.subscription_expires_at) : null;
  const daysRemaining = endDate ? Math.max(0, Math.ceil((endDate.getTime() - Date.now()) / 86400000)) : 0;

  // Format revenue in the *viewed* tenant's immutable currency (platform-admin
  // context — not the logged-in admin's tenant). Symbol + subunit factor are
  // sourced from the shared single-source helpers; the `en-IN` grouping with no
  // explicit fraction digits is preserved so the INR path is byte-identical.
  const tenantCurrency = toCurrencyCode(tenant.default_currency);
  const revenueDisplay = tenant.total_revenue != null
    ? `${getCurrencySymbol(tenantCurrency)}${(tenant.total_revenue / getSubunitMultiplier(tenantCurrency)).toLocaleString('en-IN')}`
    : '—';

  // Build status action dropdown items — role-gated
  const statusActions: Array<{ label: string; icon?: React.ReactNode; onClick: () => void; danger?: boolean; dividerBefore?: boolean }> = [];

  // Provision requires ADMIN+ (POST /tenants/:id/provision is @Roles('ADMIN'))
  if (isAdminPlus && tenant.status === 'PENDING_APPROVAL') {
    statusActions.push({
      label: 'Approve & Provision',
      icon: (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M5 13l4 4L19 7" />
        </svg>
      ),
      onClick: () => provisionMutation.mutate(),
    });
  }
  if (tenant.status === 'SUSPENDED') {
    statusActions.push({
      label: 'Reactivate Tenant',
      icon: (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      onClick: () => setConfirmAction('reactivate'),
    });
  }
  if (tenant.status !== 'SUSPENDED' && tenant.status !== 'CANCELLED' && tenant.status !== 'CHURNED') {
    statusActions.push({
      label: 'Suspend Tenant',
      onClick: () => setConfirmAction('suspend'),
    });
  }
  // Cancel requires ADMIN+ (destructive, hard to reverse)
  if (isAdminPlus && tenant.status !== 'CANCELLED' && tenant.status !== 'CHURNED') {
    statusActions.push({
      label: 'Cancel Tenant',
      onClick: () => setConfirmAction('cancel'),
      danger: true,
      dividerBefore: true,
    });
  }

  function setTab(tabId: string) {
    const params = new URLSearchParams(searchParams);
    params.set('tab', tabId);
    setSearchParams(params);
  }

  return (
    <div className="animate-fade-in">
      {/* Provision Banner */}
      {tenant.status === 'PENDING_APPROVAL' && (
        <TenantProvisionBanner
          onProvision={() => provisionMutation.mutate()}
          onReject={() => statusMutation.mutate({ status: 'CANCELLED' })}
          loading={provisionMutation.isPending}
          canApprove={isAdminPlus}
        />
      )}

      {/* Hero Header — glass panel with tenant info + actions */}
      <div className={cn(GLASS_PANEL, 'p-5 mb-6')}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-indigo-500 text-white font-bold text-lg">
              {tenant.business_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
                {tenant.business_name}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={tenant.business_type === 'RETAIL' ? 'info' : 'success'}>
                  {tenant.business_type}
                </Badge>
                <TenantStatusBadge status={tenant.status} />
                {tenant.subscription_plan && (
                  <Badge variant="info">{tenant.subscription_plan}</Badge>
                )}
              </div>
            </div>
          </div>
          {statusActions.length > 0 && (
            <Dropdown
              trigger={
                <Button variant="secondary" size="sm">
                  Actions
                  <svg className="ml-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </Button>
              }
              items={statusActions}
              align="right"
            />
          )}
        </div>
      </div>

      {/* KPI Row — spec: locations, users, total revenue, monthly bills.
          Products/Categories are totals only — the platform view never lists a
          tenant's catalogue (issue #303). */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <KpiStatCard
          label="Locations"
          value={tenant.location_count ?? 0}
          supporting={limitSupporting(tenant.location_count, limits?.max_locations)}
          tone={limitTone(tenant.location_count, limits?.max_locations, 'info')}
          icon={<MapPinIcon />}
          onClick={() => setTab('locations')}
        />
        <KpiStatCard
          label="Users"
          value={tenant.user_count ?? 0}
          supporting={limitSupporting(tenant.user_count, limits?.max_users)}
          tone={limitTone(tenant.user_count, limits?.max_users, 'brand')}
          icon={<UsersIcon />}
          onClick={() => setTab('users')}
        />
        <KpiStatCard
          label="Products"
          value={tenant.product_count ?? 0}
          tone="teal"
          icon={<CubeIcon />}
        />
        <KpiStatCard
          label="Categories"
          value={tenant.category_count ?? 0}
          tone="violet"
          icon={<TagIcon />}
        />
        <KpiStatCard
          label="Total Revenue"
          value={revenueDisplay}
          tone="success"
          icon={<CurrencyIcon />}
        />
        <KpiStatCard
          label="Monthly Bills"
          value={tenant.monthly_bills ?? 0}
          supporting={daysRemaining > 0 ? `${daysRemaining} days remaining` : undefined}
          tone="warning"
          icon={<ReceiptIcon />}
        />
      </div>

      {/* This month's usage against the plan limits (and any overrides) */}
      <TenantUsageCard tenantId={id!} />

      {/* Tabs */}
      <Tabs
        items={TABS}
        activeId={activeTab}
        onChange={setTab}
        scrollable
      />

      <div className="mt-6">
        {activeTab === 'overview' && <TenantOverviewTab tenant={tenant} />}
        {activeTab === 'users' && <TenantUsersTab tenantId={id!} />}
        {activeTab === 'locations' && <TenantLocationsTab tenantId={id!} />}
        {activeTab === 'subscription' && (
          <TenantSubscriptionTab
            tenant={tenant}
            canManage={isAdminPlus}
            canCancel={isSuperAdmin}
          />
        )}
        {activeTab === 'flags' && (
          <FeatureFlagsTab
            tenantId={id!}
            flags={tenant.feature_flags ?? []}
            isAdminPlus={isAdminPlus}
          />
        )}
        {activeTab === 'site' && (
          <TenantWebsiteTab
            tenantId={id!}
            isAdminPlus={isAdminPlus}
            isSuperAdmin={isSuperAdmin}
          />
        )}
        {activeTab === 'audit' && <TenantAuditTab tenantId={id!} />}
      </div>

      {/* Confirm Dialog */}
      {confirmAction && (
        <ConfirmDialog
          open
          title={
            confirmAction === 'suspend'
              ? 'Suspend Tenant'
              : confirmAction === 'reactivate'
              ? 'Reactivate Tenant'
              : 'Cancel Tenant'
          }
          description={
            confirmAction === 'suspend'
              ? `"${tenant.business_name}" will enter read-only mode. All mutations will be blocked until reactivated.`
              : confirmAction === 'reactivate'
              ? `"${tenant.business_name}" will exit read-only mode. All mutations will be re-enabled and users will regain full access.`
              : `This will cancel "${tenant.business_name}" and begin the data lifecycle process (90-day hard delete, 120-day S3 cleanup). This action is extremely hard to reverse.`
          }
          confirmLabel={
            confirmAction === 'suspend'
              ? 'Suspend'
              : confirmAction === 'reactivate'
              ? 'Reactivate'
              : 'Cancel Tenant'
          }
          variant={confirmAction === 'reactivate' ? 'warning' : 'danger'}
          loading={statusMutation.isPending}
          onConfirm={() => {
            const nextStatus =
              confirmAction === 'suspend'
                ? 'SUSPENDED'
                : confirmAction === 'reactivate'
                ? 'ACTIVE'
                : 'CANCELLED';
            statusMutation.mutate({
              status: nextStatus,
              reason: confirmAction === 'suspend' ? suspendReason.trim() || undefined : undefined,
            });
          }}
          onCancel={() => {
            setConfirmAction(null);
            setSuspendReason('');
          }}
        >
          {confirmAction === 'suspend' && (
            <textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Reason for suspension (recorded in the audit log)"
              aria-label="Suspension reason"
              maxLength={500}
              rows={3}
              className="w-full mt-3 px-3 py-2 text-sm rounded-lg bg-white/60 dark:bg-neutral-800/60 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none shadow-sm transition-all duration-200"
            />
          )}
        </ConfirmDialog>
      )}
    </div>
  );
}

// ─── Feature Flags Tab (spec: Toggle — ADMIN+ only) ────────────────────────

function FeatureFlagsTab({
  tenantId,
  flags,
  isAdminPlus,
}: {
  tenantId: string;
  flags: string[];
  isAdminPlus: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [localFlags, setLocalFlags] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    for (const ff of ALL_FEATURE_FLAGS) {
      map[ff.key] = flags.includes(ff.key);
    }
    return map;
  });

  async function handleToggle(key: string, checked: boolean) {
    const previous = localFlags;
    const next = { ...previous, [key]: checked };
    setLocalFlags(next);
    const nextEnabled = ALL_FEATURE_FLAGS
      .map((ff) => ff.key)
      .filter((k) => next[k]);
    try {
      await tenantService.updateFeatureFlags(tenantId, nextEnabled);
      toast({
        title: `${key.replace('ENABLE_', '').replaceAll('_', ' ')} ${checked ? 'enabled' : 'disabled'}`,
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    } catch (err) {
      setLocalFlags(previous);
      toast({
        title: 'Failed to update feature flag',
        description: (err as Error).message,
        variant: 'error',
      });
    }
  }

  return (
    <div className="animate-slide-up">
      {!isAdminPlus && (
        <Alert
          variant="info"
          title="View only"
          description="Feature flags can only be modified by Admin or Super Admin roles."
          className="mb-4"
        />
      )}
      <div className={cn(GLASS_PANEL, 'p-6')}>
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-4">
          Feature Flags
        </h3>
        <div className="space-y-3">
          {ALL_FEATURE_FLAGS.map((ff) => (
            <Toggle
              key={ff.key}
              label={ff.label}
              description={ff.description}
              checked={localFlags[ff.key] ?? false}
              onChange={(checked) => handleToggle(ff.key, checked)}
              disabled={!isAdminPlus}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Page wrapper ───────────────────────────────────────────────────────────

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  // Subscribe to the same query as TenantDetailContent so the title/breadcrumb
  // update once the tenant loads. React Query dedupes — this doesn't trigger
  // a second network request.
  const { data: tenant } = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => tenantService.getById(id!),
    enabled: !!id,
  });
  const displayName = tenant?.business_name ?? 'Tenant';

  return (
    <PageContainer
      title={displayName}
      breadcrumbs={[{ label: 'Tenants', href: '/tenants' }, { label: displayName }]}
    >
      <ErrorBoundary resetKey={id}>
        <TenantDetailContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
